/**
 * Pure decisions for Stripe Radar session coverage (PAY-2001). No DOM, no timers.
 */

// Core Stripe.js (defines window.Stripe): /v3, /v3/, or a versioned /<release>/stripe.js.
// Add-ons such as /v3/buy-button.js do not define window.Stripe.
const CORE_STRIPE_JS_PATH = /^\/(v3\/?|[a-z0-9-]+\/stripe\.js)$/;

export const isCoreStripeJsUrl = (src: string): boolean => {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  return url.hostname === "js.stripe.com" && CORE_STRIPE_JS_PATH.test(url.pathname);
};

export type RadarMintState = "pending" | "no_key" | "no_stripe_js" | "minted" | "failed";
export type RadarSessionOutcome = "ok" | "timeout" | "error" | "skipped_no_key" | "skipped_no_stripe_js";

const OUTCOME_BY_STATE: Record<RadarMintState, RadarSessionOutcome> = {
  minted: "ok",
  failed: "error",
  no_key: "skipped_no_key",
  no_stripe_js: "skipped_no_stripe_js",
  pending: "timeout", // the submit cap ran out first
};

/** At the Pay click, mint again when the page loaded its own Stripe.js after the init mint gave up. */
export const shouldRetryMintAtSubmit = (params: { mintState: RadarMintState; pageStripeJs: boolean }): boolean =>
  params.mintState === "no_stripe_js" && params.pageStripeJs;

/**
 * What one submit sends and reports. The mint state when the wait ends decides the
 * outcome and the id; the mint state at the Pay click decides `readyAtSubmit`.
 */
export const describeRadarSubmit = (params: {
  mintStateAtClick: RadarMintState;
  mintStateAtWaitEnd: RadarMintState;
  mintIdAtWaitEnd: string | undefined;
}): {
  outcome: RadarSessionOutcome;
  sessionId: string | undefined;
  readyAtSubmit: boolean;
  mintDurationSet: boolean;
} => {
  const outcome = OUTCOME_BY_STATE[params.mintStateAtWaitEnd];
  return {
    outcome,
    sessionId: outcome === "ok" ? params.mintIdAtWaitEnd : undefined,
    readyAtSubmit: params.mintStateAtClick === "minted" || params.mintStateAtClick === "failed",
    mintDurationSet: outcome === "ok" || outcome === "error",
  };
};
